import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, AlertCircle, Instagram, Video, Camera, MessageCircle, Music, Twitter, User } from 'lucide-react';
import { StalkerLog } from '../../types';
import { useNavigate } from 'react-router-dom';
import { getProxyUrl, getStaticFileUrl, stalkerAPI } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import loggingService from '../../services/loggingService';

const getPlatformIcon = (platform: string, className = "w-5 h-5") => {
	switch (platform.toLowerCase()) {
		case 'instagram': return <Instagram className={`${className} text-pink-500`} />;
		case 'vsco': return <Camera className={`${className} text-gray-800 dark:text-gray-200`} />;
		case 'tiktok': return <Video className={`${className} text-cyan-500`} />;
		case 'x':
		case 'twitter': return <Twitter className={`${className} text-blue-400`} />;
		case 'snapchat': return <MessageCircle className={`${className} text-yellow-500`} />;
		case 'spotify': return <Music className={`${className} text-green-500`} />;
		case 'person': return <User className={`${className} text-indigo-500`} />;
		default: return <AlertCircle className={`${className} text-yellow-500`} />;
	}
};

const renderTitleWithArrows = (title: string, isDark: boolean) => {
	if (!title) return null;
	return title.split(/(↓|↑)/g).map((part, i) => {
		if (part === '↓') return <span key={i} className={isDark ? 'text-red-400 font-bold' : 'text-red-600 font-bold'}>↓</span>;
		if (part === '↑') return <span key={i} className={isDark ? 'text-green-400 font-bold' : 'text-green-600 font-bold'}>↑</span>;
		return <span key={i}>{part}</span>;
	});
};

type InstaSocial = {
	iu_personId: number;
	iu_username?: string | null;
	iu_name?: string | null;
	iu_profilePicUrl?: string | null;
};

const isInstagramLog = (log: StalkerLog) => (log.sl_platform || '').toLowerCase() === 'instagram';
const isVSCOLog = (log: StalkerLog) => (log.sl_platform || '').toLowerCase() === 'vsco';
const isTiktokLog = (log: StalkerLog) => (log.sl_platform || '').toLowerCase() === 'tiktok';
const isPersonLog = (log: StalkerLog) => (log.sl_platform || '').toLowerCase() === 'person';
const isInstagramSupportedChangeType = (changeType: string) => {
	const t = (changeType || '').toLowerCase();
	return [
		'followercount_changed',
		'followingcount_changed',
		'mediacount_changed',
		'profilepicurl_changed',
		'isprivate_changed',
		'name_changed',
		'bio_changed',
		'isverified_changed',
		'username_changed',
	].includes(t);
};
const isVSCOSupportedChangeType = (changeType: string) => {
	const t = (changeType || '').toLowerCase();
	return [
		'bio_changed',
		'avatarurl_changed',
		'profilelink_changed',
		'username_changed',
		'name_changed',
	].includes(t);
};
const isTiktokSupportedChangeType = (changeType: string) => {
	const t = (changeType || '').toLowerCase();
	return [
		'followercount_changed',
		'followingcount_changed',
		'videocount_changed',
		'avatarurl_changed',
		'isprivate_changed',
		'name_changed',
		'bio_changed',
		'language_changed',
		'isverified_changed',
		'username_changed',
		'tiktokid_changed',
		'secuid_changed',
	].includes(t);
};

const getFieldFromChangeType = (changeType: string) => {
	const t = (changeType || '').trim();
	if (!t.endsWith('_changed')) return null;
	return t.slice(0, -'_changed'.length);
};

const pickValue = (val: any, field: string | null) => {
	if (field && val && typeof val === 'object' && field in val) return val[field];
	return val;
};

const toNumberOrNull = (v: any): number | null => {
	if (v === null || v === undefined) return null;
	const n = typeof v === 'number' ? v : Number(v);
	return Number.isFinite(n) ? n : null;
};

const formatValue = (v: any) => {
	if (v === null || v === undefined) return '-';
	if (typeof v === 'number') return v.toLocaleString('tr-TR');
	return String(v);
};

const INITIAL_LIMIT = 100;
const LOAD_MORE_STEP = 100;

const Logs: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const navigate = useNavigate();
	const [logs, setLogs] = useState<StalkerLog[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [limit, setLimit] = useState(INITIAL_LIMIT);
	const [selectedPlatform, setSelectedPlatform] = useState<string>('');
	const [instaByPersonId, setInstaByPersonId] = useState<Record<number, InstaSocial>>({});
	const hasLoggedPageView = useRef(false);
	const loadMoreTargetRef = useRef<HTMLDivElement | null>(null);
	const loadMoreRequestedRef = useRef(false);

	const loadData = useCallback(async (nextLimit: number, mode: 'initial' | 'more' = 'initial') => {
		try {
			const params: any = { limit: nextLimit };
			if (selectedPlatform) {
				params.platform = selectedPlatform;
			}

			if (mode === 'more') {
				setLoadingMore(true);
			} else {
				setLoading(true);
			}

			const response = await stalkerAPI.logs.getAll(params);
			const data = response?.data?.data || [];
			setLogs(data);
			setHasMore(Array.isArray(data) ? data.length >= nextLimit : false);
		} catch (error) {
			console.error('Error loading logs:', error);
		} finally {
			setLoading(false);
			setLoadingMore(false);
			loadMoreRequestedRef.current = false;
		}
	}, [selectedPlatform]);

	useEffect(() => {
		loadData(limit, limit > INITIAL_LIMIT ? 'more' : 'initial');
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/stalker/logs', 'Stalker', 'Stalker Logs');
			hasLoggedPageView.current = true;
		}
	}, [loadData, limit]);

	useEffect(() => {
		// Reset paging when platform filter changes (without backend changes we can only increase limit)
		setLimit(INITIAL_LIMIT);
		setHasMore(true);
		loadMoreRequestedRef.current = false;
	}, [selectedPlatform]);

	useEffect(() => {
		const el = loadMoreTargetRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const first = entries[0];
				if (!first?.isIntersecting) return;
				if (loading || loadingMore) return;
				if (!hasMore) return;
				if (loadMoreRequestedRef.current) return;

				loadMoreRequestedRef.current = true;
				setLimit((prev) => prev + LOAD_MORE_STEP);
			},
			{ root: null, rootMargin: '200px 0px', threshold: 0.01 }
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, [hasMore, loading, loadingMore]);

	const filteredLogs = logs.filter(log => {
		const matchesSearch = globalSearchTerm === '' ||
			log.sl_changeType.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			log.sl_description?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			log.sl_platform.toLowerCase().includes(globalSearchTerm.toLowerCase());
		return matchesSearch;
	});

	const platforms = Array.from(new Set(logs.map(log => log.sl_platform)));

	const renderVscoPerson = (log: StalkerLog) => {
		const vsco = log.accountDetail;
		const username = vsco?.vu_username ? `${vsco.vu_username}` : null;
		const imgUrl = vsco?.vu_avatarUrl;

		return (
			<button
				type="button"
				onClick={() => navigate(`/stalker/socials/vsco/${vsco?.vu_id ?? log.sl_personId}`)}
				className={`flex items-center space-x-2.5 text-[12px] font-mono hover:underline group ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}
				title="GO_TO_TARGET"
			>
				{imgUrl ? (
					<img
						src={imgUrl}
						alt={vsco?.vu_username || 'vsco'}
						className={`w-8 h-8 object-cover border ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
					/>
				) : (
					<span className={`w-8 h-8 inline-block border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`} />
				)}
				<span className="truncate max-w-[150px]">
					@{username || `ID:${log.sl_personId}`}
				</span>
			</button>
		);
	};

	const renderInstagramPerson = (log: StalkerLog) => {
		const insta = log.accountDetail;
		const username = insta?.iu_username ? `${insta.iu_username}` : null;
		const imgUrl = insta?.iu_profilePicUrl ? (getProxyUrl(insta.iu_profilePicUrl) || '') : '';

		return (
			<button
				type="button"
				onClick={() => navigate(`/stalker/socials/instagram/${insta?.iu_id ?? log.sl_personId}`)}
				className={`flex items-center space-x-2.5 text-[12px] font-mono hover:underline group ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}
				title="GO_TO_TARGET"
			>
				{imgUrl ? (
					<img
						src={imgUrl}
						alt={insta?.iu_username || 'instagram'}
						className={`w-8 h-8 object-cover border ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
					/>
				) : (
					<span className={`w-8 h-8 inline-block border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`} />
				)}
				<span className="truncate max-w-[150px]">
					@{username || `ID:${log.sl_personId}`}
				</span>
			</button>
		);
	};

	const renderTiktokPerson = (log: StalkerLog) => {
		const tiktok = log.accountDetail;
		const username = tiktok?.tu_username ? `${tiktok.tu_username}` : null;
		const imgUrl = tiktok?.tu_avatarUrl ? (getProxyUrl(tiktok.tu_avatarUrl) || '') : '';

		return (
			<button
				type="button"
				onClick={() => navigate(`/stalker/socials/tiktok/${tiktok?.tu_id ?? log.sl_personId}`)}
				className={`flex items-center space-x-2.5 text-[12px] font-mono hover:underline group ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}
				title="GO_TO_TARGET"
			>
				{imgUrl ? (
					<img
						src={imgUrl}
						alt={tiktok?.tu_username || 'tiktok'}
						className={`w-8 h-8 object-cover border ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
					/>
				) : (
					<span className={`w-8 h-8 inline-block border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`} />
				)}
				<span className="truncate max-w-[150px]">
					@{username || `ID:${log.sl_personId}`}
				</span>
			</button>
		);
	};

	const renderPerson = (log: StalkerLog) => {
		const person = log.accountDetail;
		const name = `${person.person_name + " " + person.person_surname}`;

		return (
			<button
				type="button"
				onClick={() => navigate(`/stalker/persons/${log.sl_personId}`)}
				className={`flex items-center space-x-2.5 text-[12px] font-mono hover:underline group ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}
				title="GO_TO_TARGET"
			>
				{person?.person_avatar ? (
					<img
						src={getStaticFileUrl(person.person_avatar) || ''}
						alt={name}
						className={`w-8 h-8 object-cover border ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
					/>
				) : (
					<span className={`w-8 h-8 inline-block border flex items-center justify-center text-[8px] ${isDark ? 'bg-[#1a1a1a] border-[#333] text-neutral-500' : 'bg-neutral-100 border-neutral-200 text-neutral-400'}`}>
						<User className="w-4 h-4" />
					</span>
				)}
				<span className="truncate max-w-[150px]">
					{name || `ID:${log.sl_personId}`}
				</span>
			</button>
		);
	};

	const renderInstagramDetails = (log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		// A/B/C: count changes
		if (['followercount_changed', 'followingcount_changed', 'mediacount_changed'].includes(changeType)) {
			const oldN = toNumberOrNull(oldRaw);
			const newN = toNumberOrNull(newRaw);
			const isDecrease = oldN !== null && newN !== null ? oldN > newN : false;
			const leftColor = isDecrease
				? (isDark ? 'text-green-400' : 'text-green-600')
				: (isDark ? 'text-red-400' : 'text-red-600');
			const rightColor = isDecrease
				? (isDark ? 'text-red-400' : 'text-red-600')
				: (isDark ? 'text-green-400' : 'text-green-600');

			const label =
				changeType === 'followercount_changed' ? 'Profile Followers' :
					changeType === 'followingcount_changed' ? 'Profile Followings' :
						'Profile Medias';

			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					{label}:{' '}
					<span className={`font-semibold ${leftColor}`}>{formatValue(oldN ?? oldRaw)}</span>
					<span className={`mx-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>
					<span className={`font-semibold ${rightColor}`}>{formatValue(newN ?? newRaw)}</span>
				</p>
			);
		}

		// D: profile picture changed
		if (changeType === 'profilepicurl_changed') {
			const oldUrl = typeof oldRaw === 'string' ? (getProxyUrl(oldRaw) || oldRaw) : '';
			const newUrl = typeof newRaw === 'string' ? (getProxyUrl(newRaw) || newRaw) : '';
			return (
				<div className="flex items-center space-x-3">
					<img
						src={oldUrl || ''}
						alt="old"
						className="w-16 h-16 rounded-lg object-cover border border-gray-300 dark:border-gray-700"
					/>
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>
					<img
						src={newUrl || ''}
						alt="new"
						className="w-16 h-16 rounded-lg object-cover border border-gray-300 dark:border-gray-700"
					/>
				</div>
			);
		}

		// E: private changed
		if (changeType === 'isprivate_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			const oldLocked = oldV === '1';
			const newLocked = newV === '1';
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile: {oldLocked ? '🔒' : '🔓'} <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span> {newLocked ? '🔒' : '🔓'}
				</p>
			);
		}

		// F: name changed
		if (changeType === 'name_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Name: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		// G: bio changed
		if (changeType === 'bio_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Bio: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		// H: verified changed
		if (changeType === 'isverified_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			const oldVerified = oldV === '1';
			const newVerified = newV === '1';
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Verification:{' '}
					<span className="font-medium">
						{oldVerified ? 'Verified ✅' : 'Unverified'}
					</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">
						{newVerified ? 'Verified ✅' : 'Unverified'}
					</span>
				</p>
			);
		}

		// J: Username changed
		if (changeType === 'username_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Username: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		return null;
	};

	const renderVSCODetails = (log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		// Bio changed
		if (changeType === 'bio_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Bio: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		// Avatar URL changed
		if (changeType === 'avatarurl_changed') {
			const oldUrl = typeof oldRaw === 'string' ? oldRaw : '';
			const newUrl = typeof newRaw === 'string' ? newRaw : '';
			return (
				<div className="flex items-center space-x-3">
					<img
						src={oldUrl || ''}
						alt="old"
						className="w-16 h-16 rounded-lg object-cover border border-gray-300 dark:border-gray-700"
					/>
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>
					<img
						src={newUrl || ''}
						alt="new"
						className="w-16 h-16 rounded-lg object-cover border border-gray-300 dark:border-gray-700"
					/>
				</div>
			);
		}

		// Profile link changed
		if (changeType === 'profilelink_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Link:{' '}
					<span className="font-medium break-all">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium break-all">{formatValue(newRaw)}</span>
				</p>
			);
		}

		// Username changed
		if (changeType === 'username_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Username: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		// Name changed
		if (changeType === 'name_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Name: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		return null;
	};

	const renderTiktokDetails = (log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		// A/B/C: count changes
		if (['followercount_changed', 'followingcount_changed', 'videocount_changed'].includes(changeType)) {
			const oldN = toNumberOrNull(oldRaw);
			const newN = toNumberOrNull(newRaw);
			const isDecrease = oldN !== null && newN !== null ? oldN > newN : false;
			const leftColor = isDecrease
				? (isDark ? 'text-green-400' : 'text-green-600')
				: (isDark ? 'text-red-400' : 'text-red-600');
			const rightColor = isDecrease
				? (isDark ? 'text-red-400' : 'text-red-600')
				: (isDark ? 'text-green-400' : 'text-green-600');

			const label =
				changeType === 'followercount_changed' ? 'Profile Followers' :
					changeType === 'followingcount_changed' ? 'Profile Followings' :
						'Profile Medias';

			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					{label}:{' '}
					<span className={`font-semibold ${leftColor}`}>{formatValue(oldN ?? oldRaw)}</span>
					<span className={`mx-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>
					<span className={`font-semibold ${rightColor}`}>{formatValue(newN ?? newRaw)}</span>
				</p>
			);
		}

		// D: profile picture changed
		if (changeType === 'avatarurl_changed') {
			const oldUrl = typeof oldRaw === 'string' ? (oldRaw) : '';
			const newUrl = typeof newRaw === 'string' ? (newRaw) : '';
			return (
				<div className="flex items-center space-x-3">
					<img
						src={oldUrl || ''}
						alt="old"
						className="w-16 h-16 rounded-lg object-cover border border-gray-300 dark:border-gray-700"
					/>
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>
					<img
						src={newUrl || ''}
						alt="new"
						className="w-16 h-16 rounded-lg object-cover border border-gray-300 dark:border-gray-700"
					/>
				</div>
			);
		}

		// E: private changed
		if (changeType === 'isprivate_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			const oldLocked = oldV === '1';
			const newLocked = newV === '1';
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile: {oldLocked ? '🔒' : '🔓'} <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span> {newLocked ? '🔒' : '🔓'}
				</p>
			);
		}

		// F: name changed
		if (changeType === 'name_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Name: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		// G: bio changed
		if (changeType === 'bio_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Bio: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		// H: verified changed
		if (changeType === 'isverified_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			const oldVerified = oldV === '1';
			const newVerified = newV === '1';
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Verification:{' '}
					<span className="font-medium">
						{oldVerified ? 'Verified ✅' : 'Unverified'}
					</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">
						{newVerified ? 'Verified ✅' : 'Unverified'}
					</span>
				</p>
			);
		}

		// J: username changed
		if (changeType === 'username_changed') {
			return (
				<p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
					Profile Username: <span className="font-medium">{formatValue(oldRaw)}</span>{' '}
					<span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-&gt;</span>{' '}
					<span className="font-medium">{formatValue(newRaw)}</span>
				</p>
			);
		}

		return null;
	};

	const instagramTitle = useCallback((log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		if (changeType === 'followercount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Follower Count ↓' : 'Follower Count ↑';
		}
		if (changeType === 'followingcount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Following Count ↓' : 'Following Count ↑';
		}
		if (changeType === 'mediacount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Media Count ↓' : 'Media Count ↑';
		}
		if (changeType === 'profilepicurl_changed') return 'Profile Picture Changed';
		if (changeType === 'isprivate_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			if (oldV === '0' && newV === '1') return 'Profile Locked';
			if (oldV === '1' && newV === '0') return 'Profile Unlocked';
			return 'Profile Privacy Changed';
		}
		if (changeType === 'name_changed') return 'Profile Name Changed';
		if (changeType === 'bio_changed') return 'Profile Bio Changed';
		if (changeType === 'isverified_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			if (oldV === '1' && newV === '0') return 'Profile Unverified';
			if (oldV === '0' && newV === '1') return 'Profile Verified';
			return 'Profile Verification Changed';
		}
		return log.sl_changeType;
	}, []);

	const tiktokTitle = useCallback((log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		if (changeType === 'followercount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Follower Count ↓' : 'Follower Count ↑';
		}
		if (changeType === 'followingcount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Following Count ↓' : 'Following Count ↑';
		}
		if (changeType === 'videocount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Media Count ↓' : 'Media Count ↑';
		}
		if (changeType === 'avatarurl_changed') return 'Profile Picture Changed';
		if (changeType === 'isprivate_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			if (oldV === '0' && newV === '1') return 'Profile Locked';
			if (oldV === '1' && newV === '0') return 'Profile Unlocked';
			return 'Profile Privacy Changed';
		}
		if (changeType === 'username_changed') return 'Profile Username Changed';
		if (changeType === 'name_changed') return 'Profile Name Changed';
		if (changeType === 'bio_changed') return 'Profile Bio Changed';
		if (changeType === 'isverified_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			if (oldV === '1' && newV === '0') return 'Profile Unverified';
			if (oldV === '0' && newV === '1') return 'Profile Verified';
			return 'Profile Verification Changed';
		}
		return log.sl_changeType;
	}, []);

	const vscoTitle = useCallback((log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		if (changeType === 'avatarurl_changed') return 'Profile Picture Changed';
		if (changeType === 'username_changed') return 'Profile Username Changed';
		if (changeType === 'name_changed') return 'Profile Name Changed';
		if (changeType === 'bio_changed') return 'Profile Bio Changed';
		if (changeType === 'profilelink_changed') return 'Profile Link Changed';
		return log.sl_changeType;
	}, []);

	if (loading && limit === INITIAL_LIMIT) {
		return (
			<div className={`flex flex-col items-center justify-center h-64 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
				<div className="flex items-center space-x-2">
					<div className="w-1 h-3 bg-current animate-pulse" />
					<span>[ FETCHING_STALKER_LOGS... ]</span>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4 px-2 md:px-0 pb-20">
			{/* Header */}
			<div className={`flex flex-col md:flex-row items-start justify-between md:items-center p-4 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} gap-4`}>
				<div>
					<h1 className={`text-sm font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
						// SYS: STALKER_LOGS
					</h1>
					<p className={`text-[10px] font-mono mt-0.5 uppercase tracking-wider ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
						{globalSearchTerm ? `MATCHING_RECORDS: ${filteredLogs.length} / ${logs.length}` : `TOTAL_RECORDS: ${logs.length}`}
					</p>
				</div>
				{platforms.length > 0 && (
					<div className="flex items-center space-x-2 self-end md:self-auto flex-wrap gap-2">
						<select
							value={selectedPlatform}
							onChange={(e) => setSelectedPlatform(e.target.value)}
							className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] text-neutral-400 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-300 text-neutral-600 focus:border-emerald-500/50'}`}
						>
							<option value="">ALL_PLATFORMS</option>
							{platforms.map(platform => (
								<option key={platform} value={platform}>{platform.toUpperCase()}</option>
							))}
						</select>
					</div>
				)}
			</div>

			<div className="space-y-2">
				{filteredLogs.map((log) => (
					<div key={log.sl_id} className={`p-4 border transition-colors ${isDark ? 'bg-[#111] hover:bg-[#1a1a1a] border-[#222] hover:border-[#333]' : 'bg-white hover:bg-neutral-50 border-neutral-200'}`}>
						<div className="flex flex-col md:flex-row md:items-start gap-3">
							{/* Status Icon & Title */}
							<div className="flex-1 min-w-0">
								<div className="flex items-start space-x-3 mb-2">
									<div className={`mt-0.5 p-1.5 flex-shrink-0 border flex items-center justify-center ${isDark ? 'bg-[#0a0a0a] border-[#333]' : 'bg-neutral-100 border-neutral-200'}`}>
										{getPlatformIcon(log.sl_platform, "w-4 h-4")}
									</div>
									<div className="flex-1 min-w-0">
										<h3 className={`text-[11px] font-mono font-bold tracking-wider uppercase truncate ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
											{renderTitleWithArrows(
												isInstagramLog(log) && isInstagramSupportedChangeType(log.sl_changeType)
													? instagramTitle(log)
													: isTiktokLog(log) && isTiktokSupportedChangeType(log.sl_changeType)
														? tiktokTitle(log)
														: isVSCOLog(log) && isVSCOSupportedChangeType(log.sl_changeType)
															? vscoTitle(log)
															: log.sl_changeType,
												isDark
											)}
										</h3>
										
										{/* Details/Description block */}
										<div className="mt-2 text-[10px] font-mono">
											{log.sl_description && (
												<p className={`mb-2 leading-tight ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
													{(!isInstagramLog(log) || !isInstagramSupportedChangeType(log.sl_changeType)) &&
														(!isTiktokLog(log) || !isTiktokSupportedChangeType(log.sl_changeType)) &&
														(!isVSCOLog(log) || !isVSCOSupportedChangeType(log.sl_changeType)) &&
														log.sl_description}
												</p>
											)}
											
											{isInstagramLog(log) && isInstagramSupportedChangeType(log.sl_changeType) ? (
												<div>{renderInstagramDetails(log)}</div>
											) : isTiktokLog(log) && isTiktokSupportedChangeType(log.sl_changeType) ? (
												<div>{renderTiktokDetails(log)}</div>
											) : isVSCOLog(log) && isVSCOSupportedChangeType(log.sl_changeType) ? (
												<div>{renderVSCODetails(log)}</div>
											) : (log.sl_oldValue || log.sl_newValue) ? (
												<div className={`p-2 border mt-2 space-y-1 ${isDark ? 'bg-[#0a0a0a] border-[#333]' : 'bg-neutral-50 border-neutral-200'}`}>
													{log.sl_oldValue && (
														<div className="flex items-start space-x-2">
															<span className={`flex-shrink-0 font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>[-]</span>
															<span className={`break-all ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
																{typeof log.sl_oldValue === 'object' ? JSON.stringify(log.sl_oldValue) : log.sl_oldValue}
															</span>
														</div>
													)}
													{log.sl_newValue && (
														<div className="flex items-start space-x-2">
															<span className={`flex-shrink-0 font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>[+]</span>
															<span className={`break-all ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
																{typeof log.sl_newValue === 'object' ? JSON.stringify(log.sl_newValue) : log.sl_newValue}
															</span>
														</div>
													)}
												</div>
											) : null}
										</div>
									</div>
								</div>
							</div>

							{/* Timestamp & Person Link */}
							<div className={`flex flex-col items-end md:items-start space-y-2 md:w-64 flex-shrink-0 pt-3 md:pt-0 ${isDark ? 'border-[#333]' : 'border-neutral-200'} md:border-l md:pl-4 border-t md:border-t-0`}>
								<div className={`text-[11px] font-mono tracking-widest uppercase flex items-center space-x-1.5 ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
									<Calendar className="w-3.5 h-3.5" />
									<span>{new Date(log.created_at).toLocaleString('tr-TR')}</span>
								</div>
								
								<div className="mt-1 w-full flex justify-end md:justify-start">
									{isInstagramLog(log) ? (
										renderInstagramPerson(log)
									) : isTiktokLog(log) ? (
										renderTiktokPerson(log)
									) : isVSCOLog(log) ? (
										renderVscoPerson(log)
									) : isPersonLog(log) ? (
										renderPerson(log)
									) : (
										<span className={`px-2 py-1 text-[9px] font-mono border ${isDark ? 'border-[#333] text-neutral-400' : 'border-neutral-200 text-neutral-500'}`}>
											ID:{log.sl_personId}
										</span>
									)}
								</div>
							</div>
						</div>
					</div>
				))}

				{/* Infinite scroll observer target */}
				{logs.length > 0 && (
					<div ref={loadMoreTargetRef} className="flex justify-center py-6 min-h-[48px]">
						{loadingMore ? (
							<div className="flex items-center space-x-2 font-mono text-[10px] uppercase tracking-widest text-emerald-500">
								<div className="w-1 h-3 bg-current animate-pulse" />
								<span>[ FETCHING_ARCHIVES... ]</span>
							</div>
						) : hasMore ? (
							<span className={`font-mono text-[9px] tracking-widest uppercase ${isDark ? 'text-neutral-600' : 'text-neutral-400'} animate-pulse`}>
								[ SCROLL_TO_DECRYPT_MORE ]
							</span>
						) : (
							<span className={`font-mono text-[9px] tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
								[ END_OF_LOGS ]
							</span>
						)}
					</div>
				)}
			</div>

			{filteredLogs.length === 0 && !loading && (
				<div className={`border p-8 text-center font-mono text-xs uppercase tracking-widest ${isDark ? 'bg-[#111] border-[#222] text-neutral-600' : 'bg-white border-neutral-200 text-neutral-400'}`}>
					[ NO_LOGS_FOUND_FOR_QUERY ]
				</div>
			)}
		</div>
	);
};

export default Logs;

